import React, {
  useState,
  useRef,
  useEffect,
  ChangeEvent,
  FormEvent,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import PhoneInput, {
  isValidPhoneNumber,
  getCountries,
} from "react-phone-number-input";
import type { CountryCode } from "libphonenumber-js";
import "react-phone-number-input/style.css";
import { useAppDispatch, useAppSelector } from "../../hooks/redux/useRedux";
import {
  getIndustryThunk,
  getLocationThunk,
  getProfessionCategoriesThunk,
} from "../../redux/slices/profileSlice/asyncthunk";
import {
  selectIndustryData,
  selectLocationCode,
  selectProfessionCategories,
} from "../../redux/slices/profileSlice/ProfileSlice";
import {
  TbArrowLeft,
  TbBuilding,
  TbUser,
  TbCurrencyDollar,
  TbWorld,
  TbTool,
  TbUpload,
  TbChevronDown,
  TbPhoto,
} from "react-icons/tb";
import toast from "react-hot-toast";
import { postMarketPlaceForm } from "../../api/marketPlace/marketPlaceService";

// ── Country data ───────────────────────────────────────────────────────────────

const COUNTRY_CODES = [
  "AF",
  "AL",
  "DZ",
  "AD",
  "AO",
  "AG",
  "AR",
  "AM",
  "AU",
  "AT",
  "AZ",
  "BS",
  "BH",
  "BD",
  "BB",
  "BY",
  "BE",
  "BZ",
  "BJ",
  "BT",
  "BO",
  "BA",
  "BW",
  "BR",
  "BN",
  "BG",
  "BF",
  "BI",
  "CV",
  "KH",
  "CM",
  "CA",
  "CF",
  "TD",
  "CL",
  "CN",
  "CO",
  "KM",
  "CG",
  "CD",
  "CR",
  "HR",
  "CU",
  "CY",
  "CZ",
  "DK",
  "DJ",
  "DM",
  "DO",
  "EC",
  "EG",
  "SV",
  "GQ",
  "ER",
  "EE",
  "SZ",
  "ET",
  "FJ",
  "FI",
  "FR",
  "GA",
  "GM",
  "GE",
  "DE",
  "GH",
  "GR",
  "GD",
  "GT",
  "GN",
  "GW",
  "GY",
  "HT",
  "HN",
  "HU",
  "IS",
  "IN",
  "ID",
  "IR",
  "IQ",
  "IE",
  "IL",
  "IT",
  "JM",
  "JP",
  "JO",
  "KZ",
  "KE",
  "KI",
  "KW",
  "KG",
  "LA",
  "LV",
  "LB",
  "LS",
  "LR",
  "LY",
  "LI",
  "LT",
  "LU",
  "MG",
  "MW",
  "MY",
  "MV",
  "ML",
  "MT",
  "MH",
  "MR",
  "MU",
  "MX",
  "FM",
  "MD",
  "MC",
  "MN",
  "ME",
  "MA",
  "MZ",
  "MM",
  "NA",
  "NR",
  "NP",
  "NL",
  "NZ",
  "NI",
  "NE",
  "NG",
  "NO",
  "OM",
  "PK",
  "PW",
  "PA",
  "PG",
  "PY",
  "PE",
  "PH",
  "PL",
  "PT",
  "QA",
  "RO",
  "RU",
  "RW",
  "WS",
  "SM",
  "SA",
  "SN",
  "RS",
  "SC",
  "SL",
  "SG",
  "SK",
  "SI",
  "SB",
  "SO",
  "ZA",
  "SS",
  "ES",
  "LK",
  "SD",
  "SR",
  "SE",
  "CH",
  "SY",
  "TW",
  "TJ",
  "TZ",
  "TH",
  "TL",
  "TG",
  "TO",
  "TT",
  "TN",
  "TR",
  "TM",
  "TV",
  "UG",
  "UA",
  "AE",
  "GB",
  "US",
  "UY",
  "UZ",
  "VU",
  "VE",
  "VN",
  "YE",
  "ZM",
  "ZW",
];

const _dn = new Intl.DisplayNames(["en"], { type: "region" });

const COUNTRIES = COUNTRY_CODES.map((code) => ({
  code,
  name: _dn.of(code) ?? code,
})).sort((a, b) => a.name.localeCompare(b.name));

// ── CountrySelect ──────────────────────────────────────────────────────────────

function CountrySelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const filtered = COUNTRIES.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${inputCls} flex items-center justify-between text-left`}
      >
        <span className={value ? "text-gray-900" : "text-gray-400"}>
          {value || "Select country"}
        </span>
        <TbChevronDown
          className={`w-4 h-4 text-gray-400 shrink-0 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden"
          >
            <div className="p-2 border-b border-gray-100">
              <input
                autoFocus
                type="text"
                placeholder="Search country..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-indigo-400"
              />
            </div>
            <div className="max-h-52 overflow-y-auto">
              {filtered.length === 0 ? (
                <p className="text-sm text-gray-400 px-4 py-3 text-center">
                  No results
                </p>
              ) : (
                filtered.map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => {
                      onChange(c.name);
                      setOpen(false);
                      setSearch("");
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors text-left ${
                      value === c.name
                        ? "bg-indigo-50 text-indigo-600 font-medium"
                        : "text-gray-700"
                    }`}
                  >
                    {c.name}
                  </button>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function IndustrySelect({
  value,
  onChange,
  categories,
}: {
  value: string;
  onChange: (id: string) => void;
  categories: Array<{ id: number; title: string }>;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const filtered = categories.filter((c) =>
    c.title.toLowerCase().includes(search.toLowerCase()),
  );

  const selectedTitle = value
    ? categories.find((c) => String(c.id) === value)?.title
    : null;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${inputCls} flex items-center justify-between text-left`}
      >
        <span className={value ? "text-gray-900" : "text-gray-400"}>
          {selectedTitle || "Select industry"}
        </span>
        <TbChevronDown
          className={`w-4 h-4 text-gray-400 shrink-0 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden"
          >
            <div className="p-2 border-b border-gray-100">
              <input
                autoFocus
                type="text"
                placeholder="Search industry..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-indigo-400"
              />
            </div>
            <div className="max-h-52 overflow-y-auto">
              {filtered.length === 0 ? (
                <p className="text-sm text-gray-400 px-4 py-3 text-center">
                  No results
                </p>
              ) : (
                filtered.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => {
                      onChange(String(c.id));
                      setOpen(false);
                      setSearch("");
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors text-left ${
                      value === String(c.id)
                        ? "bg-indigo-50 text-indigo-600 font-medium"
                        : "text-gray-700"
                    }`}
                  >
                    {c.title}
                  </button>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Types ──────────────────────────────────────────────────────────────────────

interface FormState {
  business_name: string;
  description: string;
  logo_image: File | null;
  industry: string;
  country: string;
  city: string;
  year_founded: string;
  owner_name: string;
  owner_email: string;
  owner_phone: string;
  asking_price: string;
  average_monthly_revenue: string;
  average_monthly_net_profit: string;
  active_client_count: string;
  reason_for_selling: string;
  // Optional financial
  monthly_expenses: string;
  repeat_client_percentage: string;
  employee_count: string;
  contractor_count: string;
  // Social media
  website_url: string;
  instagram_url: string;
  facebook_url: string;
  linkedin_url: string;
  x_url: string;
  tiktok_url: string;
  youtube_url: string;
  google_business_profile_url: string;
  // Physical assets
  physical_lease: string;
  equipment: string;
  inventory: string;
  vehicles: string;
  office_furniture_and_fixtures: string;
  specialty_tools: string;
  business_owner?: boolean | null;
  industry_title?: string | null;
}

const INITIAL_FORM: FormState = {
  business_name: "",
  description: "",
  logo_image: null,
  industry: "",
  country: "",
  city: "",
  year_founded: "",
  owner_name: "",
  owner_email: "",
  owner_phone: "",
  asking_price: "",
  average_monthly_revenue: "",
  average_monthly_net_profit: "",
  active_client_count: "",
  reason_for_selling: "",
  monthly_expenses: "",
  repeat_client_percentage: "",
  employee_count: "",
  contractor_count: "",
  website_url: "",
  instagram_url: "",
  facebook_url: "",
  linkedin_url: "",
  x_url: "",
  tiktok_url: "",
  youtube_url: "",
  google_business_profile_url: "",
  physical_lease: "",
  equipment: "",
  inventory: "",
  vehicles: "",
  office_furniture_and_fixtures: "",
  specialty_tools: "",
  business_owner: null,
  industry_title: null,
};

// ── Helper components ──────────────────────────────────────────────────────────

function SectionHeader({
  icon,
  title,
  optional,
  open,
  onToggle,
}: {
  icon: React.ReactNode;
  title: string;
  optional?: boolean;
  open?: boolean;
  onToggle?: () => void;
}) {
  return (
    <div
      className={`flex items-center justify-between mb-5 ${onToggle ? "cursor-pointer select-none" : ""}`}
      onClick={onToggle}
    >
      <div className="flex items-center gap-2.5">
        <span className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
          {icon}
        </span>
        <h2 className="text-base font-semibold text-gray-800">{title}</h2>
        {optional && (
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
            Optional
          </span>
        )}
      </div>
      {onToggle !== undefined && (
        <TbChevronDown
          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      )}
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1.5">
        {label}
        {required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls =
  "w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm placeholder-gray-400 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all";

const noE = (e: React.KeyboardEvent<HTMLInputElement>) =>
  ["e", "E", "+", "-"].includes(e.key) && e.preventDefault();

const OPTIONAL_FINANCIAL_FIELDS: Array<keyof FormState> = [
  "monthly_expenses",
  "repeat_client_percentage",
  "employee_count",
  "contractor_count",
];

const SOCIAL_FIELDS: Array<keyof FormState> = [
  "website_url",
  "instagram_url",
  "facebook_url",
  "linkedin_url",
  "x_url",
  "tiktok_url",
  "youtube_url",
  "google_business_profile_url",
];

const ASSET_FIELDS: Array<keyof FormState> = [
  "physical_lease",
  "equipment",
  "inventory",
  "vehicles",
  "office_furniture_and_fixtures",
  "specialty_tools",
];

// ── Main component ─────────────────────────────────────────────────────────────

interface Props {
  onBack: () => void;
}

export default function AddBusinessPage({ onBack }: Props) {
  const dispatch = useAppDispatch();
  const locationCode = useAppSelector(selectLocationCode);
  const professionCategories = useAppSelector(selectProfessionCategories);

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [socialOpen, setSocialOpen] = useState(false);
  const [assetsOpen, setAssetsOpen] = useState(false);
  const [urlErrors, setUrlErrors] = useState<Record<string, string>>({});
  const fileRef = useRef<HTMLInputElement>(null);

  // Refs for validation scroll
  const phoneFieldRef = useRef<HTMLDivElement>(null);
  const emailFieldRef = useRef<HTMLDivElement>(null);
  const urlFieldRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    dispatch(getLocationThunk());
    dispatch(getProfessionCategoriesThunk());
  }, [dispatch]);

  // Auto-fill country from user's geolocation (only if not yet selected)
  useEffect(() => {
    if (!locationCode?.country_code || form.country) return;
    const name = _dn.of(String(locationCode.country_code).toUpperCase());
    if (name) setForm((prev) => ({ ...prev, country: name }));
  }, [locationCode]);

  const set =
    (field: keyof FormState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleLogo = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm((prev) => ({ ...prev, logo_image: file }));
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Phone validation
    if (!form.owner_phone || !isValidPhoneNumber(form.owner_phone)) {
      setPhoneError("Please enter a valid phone number.");
      phoneFieldRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    // Email validation
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!form.owner_email || !emailRe.test(form.owner_email)) {
      setEmailError("Please enter a valid email address.");
      emailFieldRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    // URL validation for Online Presence fields
    const isValidUrl = (url: string) => {
      try {
        new URL(url);
        return true;
      } catch {
        return false;
      }
    };
    const URL_FIELDS: Array<keyof FormState> = [
      "website_url",
      "instagram_url",
      "facebook_url",
      "linkedin_url",
      "x_url",
      "tiktok_url",
      "youtube_url",
      "google_business_profile_url",
    ];
    const newUrlErrors: Record<string, string> = {};
    URL_FIELDS.forEach((field) => {
      const val = form[field] as string;
      if (val && !isValidUrl(val)) {
        newUrlErrors[field] =
          "Please enter a valid URL (e.g. https://example.com)";
      }
    });
    if (Object.keys(newUrlErrors).length > 0) {
      setUrlErrors(newUrlErrors);
      setSocialOpen(true);
      // Scroll to first URL error after section opens
      setTimeout(() => {
        const firstErrorField = URL_FIELDS.find((f) => newUrlErrors[f]);
        if (firstErrorField && urlFieldRefs.current[firstErrorField]) {
          urlFieldRefs.current[firstErrorField]?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 250);
      return;
    }

    const fd = new FormData();

    if (form.industry) fd.append("industry", form.industry);
    fd.append("business_name", form.business_name);
    fd.append("description", form.description);
    fd.append("country", form.country);
    fd.append("city", form.city);
    fd.append("year_founded", form.year_founded);
    fd.append("owner_name", form.owner_name);
    fd.append("owner_email", form.owner_email);
    fd.append("owner_phone", form.owner_phone);
    fd.append("asking_price", form.asking_price);
    fd.append("average_monthly_revenue", form.average_monthly_revenue);
    fd.append("average_monthly_net_profit", form.average_monthly_net_profit);
    fd.append("active_client_count", form.active_client_count);
    fd.append("reason_for_selling", form.reason_for_selling);

    // Optional file
    if (form.logo_image) fd.append("logo_image", form.logo_image);

    // Optional industry FK

    // Optional financial / social / asset fields
    const optionals = [
      ...OPTIONAL_FINANCIAL_FIELDS,
      ...SOCIAL_FIELDS,
      ...ASSET_FIELDS,
    ];
    optionals.forEach((key) => {
      const val = form[key];
      if (val) fd.append(key, val as string);
    });

    try {
      setSubmitting(true);
      await postMarketPlaceForm(fd);
      toast.success("Business listed successfully!");
      onBack();
    } catch (err) {
      toast.error("Failed to submit. Please check your details and try again.");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 40 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      className="min-h-screen bg-gradient-to-br from-white to-gray-50 pb-16"
    >
      {/* Sticky top bar */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-gray-100 px-6 py-4 flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="p-2 rounded-xl hover:bg-gray-100 text-gray-600 transition-colors"
        >
          <TbArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-lg font-semibold text-gray-900">
            List Your Business
          </h1>
          <p className="text-xs text-gray-400">
            Fill in the details below to list your business for sale
          </p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="max-w-3xl mx-auto px-6 py-8 space-y-6"
      >
        {/* ── Section 1: Business Overview ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <SectionHeader
            icon={<TbBuilding className="w-4 h-4" />}
            title="Business Overview"
          />
          <div className="space-y-4">
            {/* Logo upload */}
            <Field label="Business Logo">
              <div
                onClick={() => fileRef.current?.click()}
                className="flex items-center gap-4 p-4 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/30 transition-all"
              >
                {logoPreview ? (
                  <img
                    src={logoPreview}
                    alt="logo"
                    className="w-16 h-16 rounded-xl object-cover border border-gray-200"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center shrink-0">
                    <TbPhoto className="w-7 h-7 text-gray-400" />
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    {logoPreview ? "Change logo" : "Upload logo"}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    PNG, JPG up to 5 MB
                  </p>
                </div>
                <TbUpload className="w-4 h-4 text-gray-400 ml-auto shrink-0" />
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleLogo}
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Field label="Business Name" required>
                  <input
                    className={inputCls}
                    placeholder="e.g. Sunny Hair Studio"
                    value={form.business_name}
                    onChange={set("business_name")}
                    required
                  />
                </Field>
              </div>

              <div className="sm:col-span-2">
                <Field label="Description" required>
                  <textarea
                    className={`${inputCls} min-h-[100px] resize-y`}
                    placeholder="Describe your business..."
                    value={form.description}
                    onChange={set("description")}
                    required
                  />
                </Field>
              </div>

              <Field label="Industry" required>
                <div className="relative">
                  <IndustrySelect
                    value={form.industry}
                    onChange={(id) =>
                      setForm((prev) => ({ ...prev, industry: id }))
                    }
                    categories={professionCategories}
                  />
                  <input
                    type="text"
                    required
                    value={form.industry}
                    onChange={() => {}}
                    className="absolute top-0 left-0 w-full h-full opacity-0 pointer-events-none"
                    tabIndex={-1}
                    aria-hidden="true"
                  />
                </div>
              </Field>

              <Field label="Country" required>
                <CountrySelect
                  value={form.country}
                  onChange={(name) =>
                    setForm((prev) => ({ ...prev, country: name }))
                  }
                />
              </Field>

              <Field label="City" required>
                <input
                  className={inputCls}
                  placeholder="e.g. New York"
                  value={form.city}
                  onChange={set("city")}
                  required
                />
              </Field>

              <Field label="Year Founded" required>
                <input
                  type="number"
                  step="1"
                  className={inputCls}
                  placeholder="e.g. 2019"
                  min={1900}
                  max={new Date().getFullYear()}
                  value={form.year_founded}
                  onChange={set("year_founded")}
                  required
                  onKeyDown={(e) =>
                    [".", ",", "e", "E", "+", "-"].includes(e.key) &&
                    e.preventDefault()
                  }
                />
              </Field>
            </div>
          </div>
        </div>

        {/* ── Section 2: Owner Information ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <SectionHeader
            icon={<TbUser className="w-4 h-4" />}
            title="Owner Information"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Owner Name" required>
              <input
                className={inputCls}
                placeholder="Full name"
                value={form.owner_name}
                onChange={set("owner_name")}
                required
              />
            </Field>

            <Field label="Owner Email" required>
              <div ref={emailFieldRef}>
                <input
                  type="email"
                  className={`${inputCls} ${
                    emailError
                      ? "border-red-300 ring-2 ring-red-100 focus:border-red-300"
                      : ""
                  }`}
                  placeholder="email@example.com"
                  value={form.owner_email}
                  onChange={(e) => {
                    const val = e.target.value.toLowerCase();
                    setForm((prev) => ({ ...prev, owner_email: val }));
                    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    setEmailError(
                      val && !emailRe.test(val)
                        ? "Please enter a valid email address."
                        : "",
                    );
                  }}
                  required
                />
                {emailError && (
                  <p className="text-xs text-red-500 mt-1">{emailError}</p>
                )}
              </div>
            </Field>

            <div className="sm:col-span-2">
              <Field label="Owner Phone" required>
                <div ref={phoneFieldRef}>
                  <div
                    className={`w-full rounded-xl border px-3 py-2.5 transition-all ${
                      phoneError
                        ? "border-red-300 ring-2 ring-red-100"
                        : "border-gray-200 focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100"
                    }`}
                  >
                    <PhoneInput
                      international
                      countryCallingCodeEditable={false}
                      defaultCountry={(() => {
                        const code = String(
                          locationCode?.country_code || "",
                        ).toUpperCase() as CountryCode;
                        return getCountries().includes(code)
                          ? code
                          : ("US" as CountryCode);
                      })()}
                      value={form.owner_phone || undefined}
                      onChange={(value) => {
                        setForm((prev) => ({
                          ...prev,
                          owner_phone: value ?? "",
                        }));
                        if (value) {
                          setPhoneError(
                            isValidPhoneNumber(value)
                              ? ""
                              : "Please enter a valid phone number.",
                          );
                        } else {
                          setPhoneError("");
                        }
                      }}
                      limitMaxLength
                      placeholder="+1 234 567 8900"
                      numberInputProps={{
                        className:
                          "w-full bg-transparent outline-none text-sm text-gray-800 placeholder:text-gray-400",
                      }}
                    />
                  </div>
                  {phoneError && (
                    <p className="text-xs text-red-500 mt-1">{phoneError}</p>
                  )}
                </div>
              </Field>
            </div>
          </div>
        </div>

        {/* ── Section 3: Financial Details ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <SectionHeader
            icon={<TbCurrencyDollar className="w-4 h-4" />}
            title="Financial Details"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Asking Price ($)" required>
              <input
                type="number"
                step="0.01"
                min="0"
                className={inputCls}
                placeholder="0.00"
                value={form.asking_price}
                onChange={set("asking_price")}
                onKeyDown={noE}
                required
              />
            </Field>

            <Field label="Avg Monthly Revenue ($)" required>
              <input
                type="number"
                step="0.01"
                min="0"
                className={inputCls}
                placeholder="0.00"
                value={form.average_monthly_revenue}
                onChange={set("average_monthly_revenue")}
                onKeyDown={noE}
                required
              />
            </Field>

            <Field label="Avg Monthly Net Profit ($)" required>
              <input
                type="number"
                step="0.01"
                min="0"
                className={inputCls}
                placeholder="0.00"
                value={form.average_monthly_net_profit}
                onChange={set("average_monthly_net_profit")}
                onKeyDown={noE}
                required
              />
            </Field>

            <Field label="Active Client Count" required>
              <input
                type="number"
                min="0"
                step="1"
                className={inputCls}
                placeholder="0"
                value={form.active_client_count}
                onChange={set("active_client_count")}
                onKeyDown={(e) =>
                  [".", ",", "e", "E", "+", "-"].includes(e.key) &&
                  e.preventDefault()
                }
                required
              />
            </Field>

            <div className="sm:col-span-2">
              <Field label="Reason for Selling" required>
                <textarea
                  className={`${inputCls} min-h-[80px] resize-y`}
                  placeholder="Explain why you are selling this business..."
                  value={form.reason_for_selling}
                  onChange={set("reason_for_selling")}
                  required
                />
              </Field>
            </div>

            {/* Optional financial fields */}
            <Field label="Monthly Expenses ($)">
              <input
                type="number"
                step="0.01"
                min="0"
                className={inputCls}
                placeholder="0.00"
                value={form.monthly_expenses}
                onChange={set("monthly_expenses")}
                onKeyDown={noE}
              />
            </Field>

            <Field label="Repeat Client Percentage (%)">
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                className={inputCls}
                placeholder="0.00"
                value={form.repeat_client_percentage}
                onChange={set("repeat_client_percentage")}
                onKeyDown={noE}
              />
            </Field>
            <Field label="Employee Count">
              <input
                type="number"
                min="0"
                step="1"
                className={inputCls}
                placeholder="0"
                value={form.employee_count}
                onChange={set("employee_count")}
                onKeyDown={(e) =>
                  [".", ",", "e", "E", "+", "-"].includes(e.key) &&
                  e.preventDefault()
                }
              />
            </Field>

            <Field label="Contractor Count">
              <input
                type="number"
                min="0"
                step="1"
                className={inputCls}
                placeholder="0"
                value={form.contractor_count}
                onChange={set("contractor_count")}
                onKeyDown={(e) =>
                  [".", ",", "e", "E", "+", "-"].includes(e.key) &&
                  e.preventDefault()
                }
              />
            </Field>
            <div />
          </div>
        </div>

        {/* ── Section 4: Online Presence (collapsible) ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <SectionHeader
            icon={<TbWorld className="w-4 h-4" />}
            title="Online Presence"
            optional
            open={socialOpen}
            onToggle={() => setSocialOpen((v) => !v)}
          />
          <AnimatePresence initial={false}>
            {socialOpen && (
              <motion.div
                key="social"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Website URL">
                    <div
                      ref={(el) => {
                        if (el) urlFieldRefs.current.website_url = el;
                      }}
                    >
                      <input
                        type="url"
                        className={`${inputCls} ${urlErrors.website_url ? "border-red-300 ring-2 ring-red-100 focus:border-red-300" : ""}`}
                        placeholder="https://example.com"
                        value={form.website_url}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm((prev) => ({ ...prev, website_url: v }));
                          try {
                            if (v) new URL(v);
                            setUrlErrors((prev) => ({
                              ...prev,
                              website_url: "",
                            }));
                          } catch {
                            setUrlErrors((prev) => ({
                              ...prev,
                              website_url:
                                "Please enter a valid URL (e.g. https://example.com)",
                            }));
                          }
                        }}
                      />
                      {urlErrors.website_url && (
                        <p className="text-xs text-red-500 mt-1">
                          {urlErrors.website_url}
                        </p>
                      )}
                    </div>
                  </Field>

                  <Field label="Instagram">
                    <div
                      ref={(el) => {
                        if (el) urlFieldRefs.current.instagram_url = el;
                      }}
                    >
                      <input
                        type="url"
                        className={`${inputCls} ${urlErrors.instagram_url ? "border-red-300 ring-2 ring-red-100 focus:border-red-300" : ""}`}
                        placeholder="https://instagram.com/..."
                        value={form.instagram_url}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm((prev) => ({ ...prev, instagram_url: v }));
                          try {
                            if (v) new URL(v);
                            setUrlErrors((prev) => ({
                              ...prev,
                              instagram_url: "",
                            }));
                          } catch {
                            setUrlErrors((prev) => ({
                              ...prev,
                              instagram_url:
                                "Please enter a valid URL (e.g. https://example.com)",
                            }));
                          }
                        }}
                      />
                      {urlErrors.instagram_url && (
                        <p className="text-xs text-red-500 mt-1">
                          {urlErrors.instagram_url}
                        </p>
                      )}
                    </div>
                  </Field>
                  <Field label="Facebook">
                    <div
                      ref={(el) => {
                        if (el) urlFieldRefs.current.facebook_url = el;
                      }}
                    >
                      <input
                        type="url"
                        className={`${inputCls} ${urlErrors.facebook_url ? "border-red-300 ring-2 ring-red-100 focus:border-red-300" : ""}`}
                        placeholder="https://facebook.com/..."
                        value={form.facebook_url}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm((prev) => ({ ...prev, facebook_url: v }));
                          try {
                            if (v) new URL(v);
                            setUrlErrors((prev) => ({
                              ...prev,
                              facebook_url: "",
                            }));
                          } catch {
                            setUrlErrors((prev) => ({
                              ...prev,
                              facebook_url:
                                "Please enter a valid URL (e.g. https://example.com)",
                            }));
                          }
                        }}
                      />
                      {urlErrors.facebook_url && (
                        <p className="text-xs text-red-500 mt-1">
                          {urlErrors.facebook_url}
                        </p>
                      )}
                    </div>
                  </Field>
                  <Field label="LinkedIn">
                    <div
                      ref={(el) => {
                        if (el) urlFieldRefs.current.linkedin_url = el;
                      }}
                    >
                      <input
                        type="url"
                        className={`${inputCls} ${urlErrors.linkedin_url ? "border-red-300 ring-2 ring-red-100 focus:border-red-300" : ""}`}
                        placeholder="https://linkedin.com/..."
                        value={form.linkedin_url}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm((prev) => ({ ...prev, linkedin_url: v }));
                          try {
                            if (v) new URL(v);
                            setUrlErrors((prev) => ({
                              ...prev,
                              linkedin_url: "",
                            }));
                          } catch {
                            setUrlErrors((prev) => ({
                              ...prev,
                              linkedin_url:
                                "Please enter a valid URL (e.g. https://example.com)",
                            }));
                          }
                        }}
                      />
                      {urlErrors.linkedin_url && (
                        <p className="text-xs text-red-500 mt-1">
                          {urlErrors.linkedin_url}
                        </p>
                      )}
                    </div>
                  </Field>
                  <Field label="X (Twitter)">
                    <div
                      ref={(el) => {
                        if (el) urlFieldRefs.current.x_url = el;
                      }}
                    >
                      <input
                        type="url"
                        className={`${inputCls} ${urlErrors.x_url ? "border-red-300 ring-2 ring-red-100 focus:border-red-300" : ""}`}
                        placeholder="https://x.com/..."
                        value={form.x_url}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm((prev) => ({ ...prev, x_url: v }));
                          try {
                            if (v) new URL(v);
                            setUrlErrors((prev) => ({ ...prev, x_url: "" }));
                          } catch {
                            setUrlErrors((prev) => ({
                              ...prev,
                              x_url:
                                "Please enter a valid URL (e.g. https://example.com)",
                            }));
                          }
                        }}
                      />
                      {urlErrors.x_url && (
                        <p className="text-xs text-red-500 mt-1">
                          {urlErrors.x_url}
                        </p>
                      )}
                    </div>
                  </Field>
                  <Field label="TikTok">
                    <div
                      ref={(el) => {
                        if (el) urlFieldRefs.current.tiktok_url = el;
                      }}
                    >
                      <input
                        type="url"
                        className={`${inputCls} ${urlErrors.tiktok_url ? "border-red-300 ring-2 ring-red-100 focus:border-red-300" : ""}`}
                        placeholder="https://tiktok.com/..."
                        value={form.tiktok_url}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm((prev) => ({ ...prev, tiktok_url: v }));
                          try {
                            if (v) new URL(v);
                            setUrlErrors((prev) => ({
                              ...prev,
                              tiktok_url: "",
                            }));
                          } catch {
                            setUrlErrors((prev) => ({
                              ...prev,
                              tiktok_url:
                                "Please enter a valid URL (e.g. https://example.com)",
                            }));
                          }
                        }}
                      />
                      {urlErrors.tiktok_url && (
                        <p className="text-xs text-red-500 mt-1">
                          {urlErrors.tiktok_url}
                        </p>
                      )}
                    </div>
                  </Field>
                  <Field label="YouTube">
                    <div
                      ref={(el) => {
                        if (el) urlFieldRefs.current.youtube_url = el;
                      }}
                    >
                      <input
                        type="url"
                        className={`${inputCls} ${urlErrors.youtube_url ? "border-red-300 ring-2 ring-red-100 focus:border-red-300" : ""}`}
                        placeholder="https://youtube.com/..."
                        value={form.youtube_url}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm((prev) => ({ ...prev, youtube_url: v }));
                          try {
                            if (v) new URL(v);
                            setUrlErrors((prev) => ({
                              ...prev,
                              youtube_url: "",
                            }));
                          } catch {
                            setUrlErrors((prev) => ({
                              ...prev,
                              youtube_url:
                                "Please enter a valid URL (e.g. https://example.com)",
                            }));
                          }
                        }}
                      />
                      {urlErrors.youtube_url && (
                        <p className="text-xs text-red-500 mt-1">
                          {urlErrors.youtube_url}
                        </p>
                      )}
                    </div>
                  </Field>
                  <Field label="Google Business Profile">
                    <div
                      ref={(el) => {
                        if (el)
                          urlFieldRefs.current.google_business_profile_url = el;
                      }}
                    >
                      <input
                        type="url"
                        className={`${inputCls} ${urlErrors.google_business_profile_url ? "border-red-300 ring-2 ring-red-100 focus:border-red-300" : ""}`}
                        placeholder="https://business.google.com/..."
                        value={form.google_business_profile_url}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm((prev) => ({
                            ...prev,
                            google_business_profile_url: v,
                          }));
                          try {
                            if (v) new URL(v);
                            setUrlErrors((prev) => ({
                              ...prev,
                              google_business_profile_url: "",
                            }));
                          } catch {
                            setUrlErrors((prev) => ({
                              ...prev,
                              google_business_profile_url:
                                "Please enter a valid URL (e.g. https://example.com)",
                            }));
                          }
                        }}
                      />
                      {urlErrors.google_business_profile_url && (
                        <p className="text-xs text-red-500 mt-1">
                          {urlErrors.google_business_profile_url}
                        </p>
                      )}
                    </div>
                  </Field>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ── Section 5: Physical Assets (collapsible) ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <SectionHeader
            icon={<TbTool className="w-4 h-4" />}
            title="Physical Assets"
            optional
            open={assetsOpen}
            onToggle={() => setAssetsOpen((v) => !v)}
          />
          <AnimatePresence initial={false}>
            {assetsOpen && (
              <motion.div
                key="assets"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Physical Lease ($)">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className={inputCls}
                      placeholder="0.00"
                      value={form.physical_lease}
                      onChange={set("physical_lease")}
                      onKeyDown={noE}
                    />
                  </Field>
                  <Field label="Equipment ($)">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className={inputCls}
                      placeholder="0.00"
                      value={form.equipment}
                      onChange={set("equipment")}
                      onKeyDown={noE}
                    />
                  </Field>
                  <Field label="Inventory ($)">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className={inputCls}
                      placeholder="0.00"
                      value={form.inventory}
                      onChange={set("inventory")}
                      onKeyDown={noE}
                    />
                  </Field>
                  <Field label="Vehicles ($)">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className={inputCls}
                      placeholder="0.00"
                      value={form.vehicles}
                      onChange={set("vehicles")}
                      onKeyDown={noE}
                    />
                  </Field>
                  <Field label="Office Furniture & Fixtures ($)">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className={inputCls}
                      placeholder="0.00"
                      value={form.office_furniture_and_fixtures}
                      onChange={set("office_furniture_and_fixtures")}
                      onKeyDown={noE}
                    />
                  </Field>
                  <Field label="Specialty Tools ($)">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className={inputCls}
                      placeholder="0.00"
                      value={form.specialty_tools}
                      onChange={set("specialty_tools")}
                      onKeyDown={noE}
                    />
                  </Field>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Submit row */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onBack}
            className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={submitting}
            className="px-6 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-medium hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Submitting...
              </>
            ) : (
              "List Business"
            )}
          </motion.button>
        </div>
      </form>
    </motion.div>
  );
}
